import type { ElementType } from 'react';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PersonIcon from '@mui/icons-material/Person';
import PeopleIcon from '@mui/icons-material/People';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import InventoryIcon from '@mui/icons-material/Inventory';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ChatIcon from '@mui/icons-material/Chat';
import BarChartIcon from '@mui/icons-material/BarChart';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PaymentIcon from '@mui/icons-material/Payment';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import GroupsIcon from '@mui/icons-material/Groups';
import SettingsIcon from '@mui/icons-material/Settings';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import { INVOICE_READ_PERMISSIONS } from '@/lib/invoicePermissions';

export interface NavItem {
  path: string;
  labelKey: string;
  icon: ElementType;
  /** Any of these permissions grants access. Omit for unrestricted. */
  permissions?: string[];
}

export interface NavGroup {
  id: string;
  labelKey: string;
  icon: ElementType;
  items: NavItem[];
}

export type NavEntry = NavItem | NavGroup;

/** Tenant module switches that hide a nav item independently of permissions (#2646). */
export interface TenantNavModules {
  inspectionsEnabled: boolean;
}

export function isNavGroup(entry: NavEntry): entry is NavGroup {
  return 'items' in entry;
}

export function flattenNavItems(entries: NavEntry[]): NavItem[] {
  const items: NavItem[] = [];
  for (const entry of entries) {
    if (isNavGroup(entry)) {
      items.push(...entry.items);
    } else {
      items.push(entry);
    }
  }
  return items;
}

/**
 * Permission gate AND tenant module visibility. A missing module flag stays
 * visible (`isTenantModuleEnabled`); permissions still apply on their own.
 */
export function isNavItemVisible(
  item: NavItem,
  hasAnyPermission: (permissions: string[]) => boolean,
  modules: TenantNavModules,
): boolean {
  if (item.permissions && !hasAnyPermission(item.permissions)) {
    return false;
  }
  if (item.path === '/inspections' && !modules.inspectionsEnabled) {
    return false;
  }
  return true;
}

/** Drop groups the user cannot see any item of, and items they cannot see. */
export function filterVisibleNavEntries(
  entries: NavEntry[],
  hasAnyPermission: (permissions: string[]) => boolean,
  modules: TenantNavModules,
): NavEntry[] {
  const visible: NavEntry[] = [];
  for (const entry of entries) {
    if (isNavGroup(entry)) {
      const items = entry.items.filter((item) =>
        isNavItemVisible(item, hasAnyPermission, modules),
      );
      if (items.length === 0) continue;
      visible.push({ ...entry, items });
    } else if (isNavItemVisible(entry, hasAnyPermission, modules)) {
      visible.push(entry);
    }
  }
  return visible;
}

export function splitNavPath(path: string): { pathname: string; search: string } {
  const q = path.indexOf('?');
  if (q === -1) {
    return { pathname: path, search: '' };
  }
  return { pathname: path.slice(0, q), search: path.slice(q + 1) };
}

export function navPathMatches(itemPath: string, pathname: string, search: string): boolean {
  const item = splitNavPath(itemPath);
  if (item.pathname === '/') {
    return pathname === '/';
  }
  const pathOk = pathname === item.pathname || pathname.startsWith(`${item.pathname}/`);
  if (!pathOk) {
    return false;
  }
  if (item.search === '') {
    return true;
  }
  const itemParams = new URLSearchParams(item.search);
  const locParams = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  for (const [key, value] of itemParams) {
    if (locParams.get(key) !== value) {
      return false;
    }
  }
  return true;
}

/**
 * True when `itemPath` is the most specific candidate matching the location.
 * Query-bearing inventory tabs win over `/inventory`; `/invoices/payments`
 * wins over `/invoices`.
 */
export function isNavPathActive(
  itemPath: string,
  pathname: string,
  search: string,
  candidates: readonly string[],
): boolean {
  const matching = candidates.filter((candidate) => navPathMatches(candidate, pathname, search));
  if (matching.length === 0) {
    return false;
  }
  let best = matching[0]!;
  for (const candidate of matching) {
    if (candidate.length > best.length) {
      best = candidate;
    }
  }
  return best === itemPath;
}

export const settingsNavItem: NavItem = {
  path: '/settings',
  labelKey: 'settings',
  icon: SettingsIcon,
  permissions: ['settings.view', 'automation.rules.read'],
};

/**
 * Top-bar shortcuts (#3599). Inbound documents and operational exceptions left
 * the sidebar; the same permission gates still apply. Home is not listed
 * either: the logo opens the dashboard.
 */
export const headerNavItems: NavItem[] = [
  {
    path: '/inbox/documents',
    labelKey: 'inboxDocuments',
    icon: MoveToInboxIcon,
    permissions: ['work_orders.update'],
  },
  {
    path: '/exceptions',
    labelKey: 'exceptions',
    icon: ReportProblemOutlinedIcon,
    permissions: ['scheduling.exceptions'],
  },
];

/**
 * Sidebar structure (PF-113). Quotes, inspections and approvals stay routed
 * but are not listed here. Automations stays in the sidebar: Settings has no
 * place for it yet, and dropping it would leave it reachable only by URL.
 * It sits after the groups, set apart by a divider (#3182).
 */
export const navigationEntries: NavEntry[] = [
  { path: '/messages', labelKey: 'messages', icon: ChatIcon, permissions: ['messages.read'] },
  { path: '/tasks', labelKey: 'tasks', icon: TaskAltIcon },
  { path: '/calendar', labelKey: 'calendar', icon: CalendarMonthIcon },
  {
    path: '/workorders',
    labelKey: 'workorders',
    icon: AssignmentIcon,
    permissions: ['work_orders.read'],
  },
  {
    id: 'crm',
    labelKey: 'groups.crm',
    icon: PeopleIcon,
    items: [
      {
        path: '/parties',
        labelKey: 'parties',
        icon: PersonIcon,
        permissions: ['customers.read'],
      },
      {
        path: '/vehicles',
        labelKey: 'vehicles',
        icon: DirectionsCarIcon,
        permissions: ['vehicles.read'],
      },
    ],
  },
  {
    id: 'inventory',
    labelKey: 'groups.inventory',
    icon: WarehouseIcon,
    items: [
      {
        path: '/inventory',
        labelKey: 'inventory',
        icon: InventoryIcon,
        permissions: ['inventory.read', 'purchase_orders.read'],
      },
      {
        path: '/inventory?tab=purchaseorders',
        labelKey: 'purchaseOrders',
        icon: ShoppingCartIcon,
        permissions: ['purchase_orders.read'],
      },
      {
        path: '/inventory?tab=transit',
        labelKey: 'transitWarehouse',
        icon: ImportExportIcon,
        permissions: ['inventory.read'],
      },
      {
        path: '/inventory?tab=needs',
        labelKey: 'partsDashboard',
        icon: AssessmentIcon,
        permissions: ['inventory.read'],
      },
    ],
  },
  {
    id: 'receivables',
    labelKey: 'groups.receivables',
    icon: AccountBalanceIcon,
    items: [
      {
        path: '/invoices',
        labelKey: 'invoices',
        icon: ReceiptLongIcon,
        permissions: [...INVOICE_READ_PERMISSIONS],
      },
      {
        path: '/invoices/payments',
        labelKey: 'payments',
        icon: PaymentIcon,
        permissions: ['invoices.manage'],
      },
      {
        path: '/invoices/exports',
        labelKey: 'accountingExport',
        icon: AccountBalanceIcon,
        permissions: ['reports.read', 'invoices.manage'],
      },
      {
        path: '/reports',
        labelKey: 'reports',
        icon: BarChartIcon,
        permissions: ['reports.read'],
      },
    ],
  },
  {
    id: 'hr',
    labelKey: 'groups.hr',
    icon: GroupsIcon,
    items: [
      {
        path: '/hr',
        labelKey: 'hr',
        icon: GroupsIcon,
        permissions: ['hr.view_team'],
      },
    ],
  },
];

/** Flat list of every sidebar item, including pinned Settings. */
export const navItems: NavItem[] = [...flattenNavItems(navigationEntries), settingsNavItem];
